import { api_fetch } from "../../../api/client"
import { ForecastVersion, HierarchyNode, SalesDataPoint, SalesHierarchyNode } from "../types"

// API response types (simplified versions of our frontend types)
export interface ApiForecastVersion {
  id: string
  name: string
  description?: string
  scenario_kind: "operating_plan" | "growth_story" | "board_case" | string
  parent_version_id?: string
  fiscal_year?: number
  forecast_start_period?: string
  actuals_through_period?: string
  /** `month` | `period` | `quarter` | `week` — from DB / ingest. */
  period_grain?: string
  created_at: string
  updated_at: string
  is_primary: boolean
  /** `forecast` | `budget` — each plan kind has its own primary. */
  plan_kind?: string
  locked_at?: string
  archived_at?: string
  created_by?: string
}

// API endpoints
// api_fetch already prefixes with '/api', so avoid double '/api/api' in dev
const API_BASE = '/forecast-versions'
const FORECAST_AUDIT_EVENTS_BASE = '/forecast-audit-events'

export interface CreateForecastVersionRequest {
  name: string
  description?: string
  scenario_kind?: string
  parent_version_id?: string
  fiscal_year?: number
  forecast_start_period?: string
  period_grain?: string
  is_primary?: boolean
  /** `forecast` (default) | `budget`. */
  plan_kind?: string
}

export interface UpdateForecastVersionRequest {
  name?: string
  description?: string
  scenario_kind?: string
  fiscal_year?: number
  forecast_start_period?: string
  actuals_through_period?: string
  period_grain?: string
  is_primary?: boolean
  locked?: boolean
  archived?: boolean
}

export interface ForecastSummaryResponse {
  version_id: string
  total_volume_dollars: string
  total_cases_sold: number
  total_store_count: number
  forecast_volume_dollars: string
  actual_volume_dollars: string
  /** Client-computed leaf sum of imputed promo revenue lift (plan columns); omitted when zero. */
  total_promo_lift_dollars?: number
  /** Client-computed leaf sum of incremental promo units (plan columns); omitted when zero. */
  total_promo_lift_units?: number
  top_down_target_value?: string
  top_down_variance?: string
}

export interface ForecastDiffRow {
  path_key: string
  name: string
  level: string
  from_volume_dollars: string
  to_volume_dollars: string
  volume_delta: string
  from_forecast_volume_dollars: string
  to_forecast_volume_dollars: string
  forecast_volume_delta: string
  from_actual_volume_dollars: string
  to_actual_volume_dollars: string
  actual_volume_delta: string
  from_cases_sold: number
  to_cases_sold: number
  cases_delta: number
}

export interface ForecastVersionDiffResponse {
  from_version_id: string
  to_version_id: string
  volume_delta: string
  forecast_volume_delta: string
  actual_volume_delta: string
  cases_delta: number
  rows: ForecastDiffRow[]
  summary: string
}

export interface ForecastAuditEventRow {
  id: string
  version_id: string
  org_id: string
  actor_id?: string | null
  /** Resolved on the server for changelog display. */
  actor_email?: string | null
  actor_name?: string | null
  event_type: string
  summary: string
  payload?: unknown
  created_at: string
  change_channel?: string | null
  forecast_volume_delta?: string | null
  actual_volume_delta?: string | null
  forecast_cases_delta?: number | null
  actual_cases_delta?: number | null
  /** True when reverting this row is supported (snapshot exists for the previous event on this version). */
  revert_available?: boolean
}

export interface ForecastChangelogQueryParams {
  from?: string
  to?: string
  limit?: number
  change_channel?: string
  min_abs_forecast_delta?: string
}

export interface TopDownTargetRequest {
  metric: "revenue" | "cases"
  target_value: number
  allocation_method?: "historical_mix" | "even" | string
  start_period: string
  end_period: string
}

export interface ForecastHierarchyResponse {
  nodes: SalesHierarchyNode[]
  configurations: ForecastConfigurationPayload[]
  data_points: ForecastSalesDataPayload[]
}

export interface ForecastConfigurationPayload {
  id?: string
  node_id?: string
  initial_doors: number
  initial_cases_per_month: number
  cost_per_case: number
  store_count_growth_multiplier: number
  same_store_sales_multiplier: number
}

export interface ForecastSalesDataPayload {
  period: { period_key: string }
  metrics: {
    store_count: number
    cases_sold: number
    volume_dollars: number
    case_cost: number
    units_sold?: number
    detail?: unknown
  }
  is_forecast?: boolean
  is_actual?: boolean
  source_kind?: string
  data_source?: string
  is_locked?: boolean
  manual_note?: string
  period_grain?: string
  detail_metrics?: unknown
}

export interface TopDownPreviewUpdate {
  data_point_id: string
  node_id: string
  period: string
  cases_sold: number
  volume_dollars: string | number
  source_kind: string
  manual_note: string
}

export interface TopDownPreviewResponse {
  updates: TopDownPreviewUpdate[]
  eligible_total_before: string
  scale: string
}

/** Stored in audit `payload.save_context` on hierarchy save. */
export interface ForecastHierarchySaveContext {
  driver: string
  notes?: string
}

export interface UpdateHierarchyRequest {
  nodes: SalesHierarchyNode[]
  /** Persisted on Save after a top-down preview (writes forecast_top_down_target). */
  top_down_commit?: TopDownTargetRequest
  /** Required for interactive saves; appears in changelog with actor. */
  save_context?: ForecastHierarchySaveContext
  /** Optimistic concurrency: the version `updated_at` the client loaded. The server rejects the save when stale. */
  expected_updated_at?: string
}

export interface UpdateHierarchyResponse {
  /** Echo this back as `expected_updated_at` on the next save. */
  updated_at: string
  /** True when nothing was written: the save sits in the approval queue (`change_request_id`). */
  queued?: boolean
  change_request_id?: string | null
}

/** One row of the forecast approval queue (`GET /forecast/change-requests`). */
export interface ForecastChangeRequestRow {
  id: string
  version_id: string | null
  /** Null once the version was deleted. */
  version_name: string | null
  requested_by: string
  requester_email: string | null
  requester_name: string | null
  created_at: string
  status: "pending" | "approved" | "rejected" | string
  summary: string | null
  reviewed_by: string | null
  reviewer_email: string | null
  reviewed_at: string | null
  review_note: string | null
}

export async function listForecastChangeRequests(status: "pending" | "approved" | "rejected" | "all" = "pending"): Promise<ForecastChangeRequestRow[]> {
  const result = await api_fetch<ForecastChangeRequestRow[]>("/forecast/change-requests", { method: "GET", params: { status } })
  if (!result.ok) {
    throw new Error(`Failed to load forecast change requests: ${result.error.message}`)
  }
  return result.value.data
}

/** Admin only: applies the stored save as the requester and marks the request approved. */
export async function approveForecastChangeRequest(id: string, note?: string): Promise<void> {
  const result = await api_fetch(`/forecast/change-requests/${id}/approve`, { method: "POST", body: { note } })
  if (!result.ok) {
    throw new Error(result.error.message)
  }
}

/** Admin only: closes the request without writing; the note is shown to the requester. */
export async function rejectForecastChangeRequest(id: string, note?: string): Promise<void> {
  const result = await api_fetch(`/forecast/change-requests/${id}/reject`, { method: "POST", body: { note } })
  if (!result.ok) {
    throw new Error(result.error.message)
  }
}

export interface UpdateConfigurationsRequest {
  configurations: ForecastConfigurationPayload[]
}

// Forecast Version API
export async function listForecastVersions(): Promise<ApiForecastVersion[]> {
  const result = await api_fetch<ApiForecastVersion[]>(API_BASE, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch forecast versions: ${result.error.message}`)
  }
}

export async function createForecastVersion(data: CreateForecastVersionRequest): Promise<ApiForecastVersion> {
  const result = await api_fetch<ApiForecastVersion>(API_BASE, {
    method: 'POST',
    body: data
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to create forecast version: ${result.error.message}`)
  }
}

export async function updateForecastVersion(id: string, data: UpdateForecastVersionRequest): Promise<ApiForecastVersion> {
  const result = await api_fetch<ApiForecastVersion>(`${API_BASE}/${id}`, {
    method: 'PUT',
    body: data
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to update forecast version: ${result.error.message}`)
  }
}

export async function deleteForecastVersion(id: string): Promise<void> {
  const result = await api_fetch(`${API_BASE}/${id}`, {
    method: 'DELETE'
  })
  if (!result.ok) {
    throw new Error(`Failed to delete forecast version: ${result.error.message}`)
  }
}

export async function cloneForecastVersion(id: string, data: CreateForecastVersionRequest): Promise<ApiForecastVersion> {
  const result = await api_fetch<ApiForecastVersion>(`${API_BASE}/${id}/clone`, {
    method: 'POST',
    body: data
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to clone forecast version: ${result.error.message}`)
  }
}

/**
 * Upsert a QuickBooks-customer → forecast-node mapping (idempotent on the normalized alias).
 * Fired automatically after saving a sales source whose customer was picked from the
 * accounting customer list, so actuals land on the new row without a trip to /mappings.
 */
export async function createForecastCustomerAlias(alias: string, customer_stable_key: string): Promise<void> {
  const result = await api_fetch(`/forecast-mappings/customer-aliases`, {
    method: 'POST',
    body: { alias, customer_stable_key }
  })
  if (!result.ok) {
    throw new Error(`Failed to create customer mapping: ${result.error.message}`)
  }
}

/** A trade-calendar event matched to the promotion record it was planned as. */
export interface ResolvedRemotePromotion {
  remote_promo_id: string
  promotion_id: string
  name: string
  start_date?: string | null
  end_date?: string | null
}

/**
 * Match trade-calendar events to promotions in the promo calendar, by external id.
 *
 * Ids with no promotion are simply absent from the response — a plan year that has not been
 * loaded into the promo calendar is an ordinary state, and those rows stay unlinked rather than
 * offering a link that goes nowhere. Never throws: a failed lookup should cost the links, not the
 * panel.
 */
export async function resolveRemotePromotions(remote_ids: string[]): Promise<ResolvedRemotePromotion[]> {
  if (remote_ids.length === 0) return []
  // The endpoint caps one request at 500 ids so a caller cannot build an unbounded IN list.
  // Sanzo's 2027 calendar alone has 594, so chunk rather than let the tail go silently unlinked.
  const CHUNK = 500
  const chunks: string[][] = []
  for (let index = 0; index < remote_ids.length; index += CHUNK) {
    chunks.push(remote_ids.slice(index, index + CHUNK))
  }
  const responses = await Promise.all(
    chunks.map((chunk: string[]) =>
      api_fetch<ResolvedRemotePromotion[]>(`/promotions/resolve-remote`, {
        method: 'POST',
        body: { remote_ids: chunk },
      })
    )
  )
  return responses.flatMap((result) => (result.ok ? result.value.data : []))
}

export async function getForecastSummary(id: string): Promise<ForecastSummaryResponse> {
  const result = await api_fetch<ForecastSummaryResponse>(`${API_BASE}/${id}/summary`, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch forecast summary: ${result.error.message}`)
  }
}

export async function getForecastDiff(id: string, compareId: string): Promise<ForecastVersionDiffResponse> {
  const result = await api_fetch<ForecastVersionDiffResponse>(`${API_BASE}/${id}/diff/${compareId}`, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to compare forecast versions: ${result.error.message}`)
  }
}

export interface RestoreForecastAuditRequest {
  name?: string
}

export async function restoreForecastAuditEvent(
  auditEventId: string,
  body?: RestoreForecastAuditRequest
): Promise<ApiForecastVersion> {
  const result = await api_fetch<ApiForecastVersion>(`${FORECAST_AUDIT_EVENTS_BASE}/${auditEventId}/restore`, {
    method: 'POST',
    body: body ?? {},
  })
  if (result.ok) {
    return result.value.data
  }
  throw new Error(`Failed to restore forecast snapshot: ${result.error.message}`)
}

export async function listForecastChangelog(
  versionId: string,
  params?: ForecastChangelogQueryParams
): Promise<ForecastAuditEventRow[]> {
  const search = new URLSearchParams()
  if (params?.from) {
    search.set('from', params.from)
  }
  if (params?.to) {
    search.set('to', params.to)
  }
  if (params?.limit != null) {
    search.set('limit', String(params.limit))
  }
  if (params?.change_channel) {
    search.set('change_channel', params.change_channel)
  }
  if (params?.min_abs_forecast_delta) {
    search.set('min_abs_forecast_delta', params.min_abs_forecast_delta)
  }
  const qs = search.toString()
  const url = qs.length > 0 ? `${API_BASE}/${versionId}/changelog?${qs}` : `${API_BASE}/${versionId}/changelog`
  const result = await api_fetch<ForecastAuditEventRow[]>(url, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to load forecast changelog: ${result.error.message}`)
  }
}

/** Preview top-down allocation without persisting (use Save to commit hierarchy + metadata). */
export async function previewTopDownTarget(id: string, data: TopDownTargetRequest): Promise<TopDownPreviewResponse> {
  const result = await api_fetch<TopDownPreviewResponse>(`${API_BASE}/${id}/top-down-target/preview`, {
    method: 'POST',
    body: data
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to preview top-down target: ${result.error.message}`)
  }
}

/** Applies top-down on the server immediately (prefer preview + Save from the UI). */
export async function applyTopDownTarget(id: string, data: TopDownTargetRequest): Promise<void> {
  const result = await api_fetch(`${API_BASE}/${id}/top-down-target`, {
    method: 'POST',
    body: data
  })
  if (!result.ok) {
    throw new Error(`Failed to apply top-down target: ${result.error.message}`)
  }
}

export async function exportForecastVersion(id: string, exportKind: string): Promise<Blob> {
  const response = await fetch(`/api${API_BASE}/${id}/export/${exportKind}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  if (!response.ok) {
    throw new Error(`Failed to export forecast: ${response.statusText}`)
  }
  return response.blob()
}

// Forecast Hierarchy API
export async function getForecastHierarchy(id: string): Promise<ForecastHierarchyResponse> {
  const result = await api_fetch<ForecastHierarchyResponse>(`${API_BASE}/${id}/hierarchy`, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch forecast hierarchy: ${result.error.message}`)
  }
}

// Helper function to flatten hierarchy for backend
function flattenHierarchyForBackend(nodes: SalesHierarchyNode[]): BackendHierarchyNodePayload[] {
  const flattened: BackendHierarchyNodePayload[] = []
  
  function processNode(node: ForecastNodeForBackend) {
    // Convert this node
    const convertedNode = convertFrontendNodeToBackend(node)
    flattened.push(convertedNode)
    
    // Process children recursively
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: HierarchyNode) => processNode(child as ForecastNodeForBackend))
    }
  }
  
  nodes.forEach((node: SalesHierarchyNode) => processNode(node as ForecastNodeForBackend))
  return flattened
}

export async function updateForecastHierarchy(id: string, data: UpdateHierarchyRequest): Promise<UpdateHierarchyResponse> {
  // Flatten hierarchy and convert frontend nodes to backend format
  const convertedNodes = flattenHierarchyForBackend(data.nodes)

  const body: Record<string, unknown> = { nodes: convertedNodes }
  if (data.top_down_commit) {
    body.top_down_commit = data.top_down_commit
  }
  if (data.save_context) {
    body.save_context = data.save_context
  }
  if (data.expected_updated_at) {
    body.expected_updated_at = data.expected_updated_at
  }

  const result = await api_fetch<UpdateHierarchyResponse>(`${API_BASE}/${id}/hierarchy`, {
    method: 'POST',
    body
  })
  if (!result.ok) {
    throw new Error(`Failed to update forecast hierarchy: ${result.error.message}`)
  }
  return result.value.data
}

export async function updateForecastConfigurations(id: string, data: UpdateConfigurationsRequest): Promise<void> {
  const result = await api_fetch(`${API_BASE}/${id}/configurations`, {
    method: 'PUT',
    body: data
  })
  if (!result.ok) {
    throw new Error(`Failed to update forecast configurations: ${result.error.message}`)
  }
}

export async function recalculateForecastData(id: string): Promise<void> {
  const result = await api_fetch(`${API_BASE}/${id}/recalculate`, {
    method: 'POST',
    body: {}
  })
  if (!result.ok) {
    throw new Error(`Failed to recalculate forecast data: ${result.error.message}`)
  }
}

export interface ActualizeSkips {
  unmapped_product_lines: number
  unmapped_customer_lines: number
  no_sku_node_lines: number
  /** The customers behind `unmapped_customer_lines`, most lines first (the top few). */
  unmapped_customers?: UnmappedCustomerCount[]
}

export interface UnmappedCustomerCount {
  name: string
  lines: number
}

export interface ActualizeForecastResponse {
  version_id: string
  points_written: number
  /** Invoice lines the run could not place, by reason (fix on /sales-forecasting/mappings). */
  skips: ActualizeSkips
  last_actualized_at: string
  actuals_through_period?: string
}

export async function actualizeForecastVersion(id: string): Promise<ActualizeForecastResponse> {
  const result = await api_fetch<ActualizeForecastResponse>(`${API_BASE}/${id}/actualize`, {
    method: 'POST',
    body: {}
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to actualize forecast: ${result.error.message}`)
  }
}

export interface CompareCell {
  node_id: string
  path_key?: string
  period: string
  forecast_volume_dollars?: string
  actual_volume_dollars?: string
  forecast_cases?: number
  actual_cases?: number
  forecast_units?: number
  actual_units?: number
  variance_volume_dollars?: string
  variance_volume_pct?: number
}

export interface ForecastCompareResponse {
  version_id: string
  actuals_through_period?: string
  last_actualized_at?: string
  cells: CompareCell[]
  /** Cells holding a plan number; `cells` also holds any actuals. */
  plan_cells?: number
  /** Cells holding an actual in one of the plan's months. */
  actual_cells?: number
  /** Preview only: invoice lines that did not land on this version. */
  skips?: ActualizeSkips
}

export async function getForecastCompare(id: string): Promise<ForecastCompareResponse> {
  const result = await api_fetch<ForecastCompareResponse>(`${API_BASE}/${id}/compare`, {
    method: 'GET'
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch forecast compare: ${result.error.message}`)
  }
}

/** Preview QB-derived actuals as compare cells without persisting to the forecast. */
export async function previewActualizeForecastVersion(id: string): Promise<ForecastCompareResponse> {
  const result = await api_fetch<ForecastCompareResponse>(`${API_BASE}/${id}/preview-actualize`, {
    method: 'POST',
    body: {}
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to preview actualize forecast: ${result.error.message}`)
  }
}

// Helper function to convert API version to frontend version
export function convertApiVersionToFrontend(apiVersion: ApiForecastVersion): ForecastVersion {
  return {
    id: apiVersion.id,
    name: apiVersion.name,
    description: apiVersion.description,
    scenario_kind: apiVersion.scenario_kind,
    parent_version_id: apiVersion.parent_version_id,
    fiscal_year: apiVersion.fiscal_year,
    forecast_start_period: apiVersion.forecast_start_period,
    actuals_through_period: apiVersion.actuals_through_period,
    period_grain: apiVersion.period_grain,
    locked_at: apiVersion.locked_at,
    archived_at: apiVersion.archived_at,
    created_at: apiVersion.created_at,
    updated_at: apiVersion.updated_at,
    is_primary: apiVersion.is_primary,
    plan_kind: apiVersion.plan_kind ?? "forecast",
    data: [], // Will be loaded separately
    periods: [], // Will be loaded from context
    compare_mode: false, // Will be loaded from context
    comparison_periods: [], // Will be loaded from context
    column_config: undefined // Will be loaded from context
  }
}

// Helper function to convert frontend SalesHierarchyNode to backend format
interface BackendHierarchyNodePayload {
  id?: string
  parent_id: string | null
  stable_key?: string
  path_key: string
  level: string
  name: string
  metadata: Record<string, unknown> | null
  forecast_config: ForecastConfigurationPayload
  sales_data: ForecastSalesDataPayload[]
}

type ForecastSalesDataPointForBackend = SalesDataPoint & {
  store_count?: number
  cases_sold?: number
  volume_dollars?: number
  case_cost?: number
  detail_metrics?: unknown
}

type ForecastNodeForBackend = HierarchyNode & {
  product_sku?: string
  sku_name?: string
  product_line_name?: string
  case_cost?: number
}

export function convertFrontendNodeToBackend(node: ForecastNodeForBackend): BackendHierarchyNodePayload {
  // Create metadata object with only essential data
  const metadata: Record<string, unknown> = { ...(node.metadata || {}) };
  
  // Add SKU-specific fields if they exist
  if (node.product_sku) metadata.product_sku = node.product_sku;
  if (node.sku_name) metadata.sku_name = node.sku_name;
  if (node.product_line_name) metadata.product_line_name = node.product_line_name;
  
  // Normalize forecast_config to ensure required fields exist for backend
  const firstDataPoint = Array.isArray(node.sales_data) && node.sales_data.length > 0 ? node.sales_data[0] : null
  const firstBackendDataPoint = firstDataPoint as ForecastSalesDataPointForBackend | null
  const inferredInitialDoors = Number(firstDataPoint?.metrics?.store_count ?? firstBackendDataPoint?.store_count ?? 0)
  const inferredInitialCases = Number(firstDataPoint?.metrics?.cases_sold ?? firstBackendDataPoint?.cases_sold ?? 0)
  const inferredCaseCost = Number(node.case_cost ?? firstDataPoint?.metrics?.case_cost ?? firstBackendDataPoint?.case_cost ?? 0)

  const cfg = node.forecast_config
  const normalizedForecastConfig = {
    initial_doors: Number(cfg?.initial_doors ?? inferredInitialDoors ?? 0),
    initial_cases_per_month: Number(cfg?.initial_cases_per_month ?? inferredInitialCases ?? 0),
    cost_per_case: Number(cfg?.cost_per_case ?? inferredCaseCost ?? 0),
    same_store_sales_multiplier: Number(cfg?.same_store_sales_multiplier ?? 0.03),
    store_count_growth_multiplier: Number(cfg?.store_count_growth_multiplier ?? 0.02),
  }

  const hasChildren = Array.isArray(node.children) && node.children.length > 0

  // Transform source leaf sales data back to backend format. Parent rollups are derived in the UI/server
  // and should not be persisted as duplicate data points.
  const transformedSalesData: ForecastSalesDataPayload[] = hasChildren ? [] : (node.sales_data || []).map((dataPoint) => {
    const backendDataPoint = dataPoint as ForecastSalesDataPointForBackend
    const metrics = dataPoint.metrics || {}
    const detail = metrics.detail ?? backendDataPoint.detail_metrics
    const transformed: ForecastSalesDataPayload = {
      period: { period_key: String(dataPoint.period?.period_key ?? dataPoint.period ?? "") },
      metrics: {
        store_count: metrics.store_count ?? backendDataPoint.store_count ?? 0,
        cases_sold: metrics.cases_sold ?? backendDataPoint.cases_sold ?? 0,
        volume_dollars: metrics.volume_dollars ?? backendDataPoint.volume_dollars ?? 0,
        case_cost: metrics.case_cost ?? backendDataPoint.case_cost ?? 0,
        ...(metrics.units_sold !== undefined && metrics.units_sold !== null
          ? { units_sold: metrics.units_sold }
          : {}),
      },
      ...(detail && typeof detail === "object" && Object.keys(detail).length > 0
        ? { detail_metrics: detail }
        : {}),
      is_forecast: dataPoint.is_forecast,
      is_actual: dataPoint.is_actual,
      source_kind: dataPoint.source_kind || (dataPoint.is_actual ? "actual" : dataPoint.is_forecast ? "forecast" : "imported"),
      data_source: dataPoint.data_source,
      is_locked: dataPoint.is_locked,
      manual_note: dataPoint.manual_note || (dataPoint.opportunity_base_metrics ? JSON.stringify({
        kind: "opportunity_base_metrics",
        metrics: dataPoint.opportunity_base_metrics
      }) : undefined)
      // Don't spread the original dataPoint as it might contain conflicting properties
    }
    
    
    return transformed
  })

  const converted: BackendHierarchyNodePayload = {
    id: node.id, // Send ID but backend will ignore it and generate new one
    parent_id: node.parent_id || null,
    stable_key: node.stable_key || node.id,
    path_key: node.path_key || buildPathKey(node),
    level: node.level,
    name: node.name,
    metadata: Object.keys(metadata).length > 0 ? metadata : null,
    // Include forecast configuration and sales data
    forecast_config: normalizedForecastConfig,
    sales_data: transformedSalesData
    // created_at and updated_at will be handled by the backend
  }
  
  
  return converted
}

function buildPathKey(node: ForecastNodeForBackend): string {
  const keyPart = `${node.level}:${String(node.name || "").toLowerCase().trim()}`
  return node.parent_id ? `${node.parent_id}/${keyPart}` : keyPart
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("convertFrontendNodeToBackend", () => {
    it("preserves explicit zero metric values", () => {
      const node = {
        id: "leaf",
        level: "sku",
        name: "Zero row",
        parent_id: null,
        stable_key: "sku:zero",
        path_key: "customer/sku:zero",
        metadata: {},
        sales_data: [
          {
            period: { period_key: "2026-01", year: 2026, month: 1 },
            metrics: {
              store_count: 0,
              cases_sold: 0,
              volume_dollars: 0,
              case_cost: 0,
              units_sold: 0,
            },
            source_kind: "forecast",
          },
        ],
        children: [],
      } as unknown as SalesHierarchyNode

      const out = convertFrontendNodeToBackend(node)

      expect(out.sales_data[0].metrics.store_count).toBe(0)
      expect(out.sales_data[0].metrics.cases_sold).toBe(0)
      expect(out.sales_data[0].metrics.volume_dollars).toBe(0)
      expect(out.sales_data[0].metrics.case_cost).toBe(0)
      expect(out.sales_data[0].metrics.units_sold).toBe(0)
    })
  })
}
